import type { EnvironmentId } from "@t3tools/contracts";
import { createStore, del, get, promisifyRequest, set, type UseStore } from "idb-keyval";

import { accountDatabaseName } from "../accountStorage";
import type { VoiceTranscriptionFailure } from "../voiceTranscription";

export type VoiceClipStatus = "recording" | "pending" | "transcribing" | "failed" | "transcribed";

export interface VoiceClipRecord {
  readonly id: string;
  readonly targetKey: string;
  readonly environmentId: EnvironmentId;
  readonly createdAt: number;
  readonly mimeType: string;
  readonly durationMs: number;
  readonly sizeBytes: number;
  readonly status: VoiceClipStatus;
  readonly failure: VoiceTranscriptionFailure | null;
  readonly transcript: string | null;
}

export interface VoiceClipPersistence {
  readonly loadRecords: () => Promise<VoiceClipRecord[]>;
  readonly loadRecord: (id: string) => Promise<VoiceClipRecord | undefined>;
  readonly saveRecord: (record: VoiceClipRecord) => Promise<void>;
  readonly appendChunk: (id: string, index: number, chunk: Blob) => Promise<void>;
  readonly loadChunks: (id: string) => Promise<Blob[]>;
  readonly saveAudio: (id: string, audio: Blob) => Promise<void>;
  readonly loadAudio: (id: string) => Promise<Blob | undefined>;
  readonly remove: (id: string) => Promise<void>;
}

export type VoiceClipLockRelease = () => Promise<void>;

export interface VoiceClipCoordination {
  readonly hold: (id: string) => Promise<VoiceClipLockRelease | null>;
  readonly withLock: <T>(id: string, run: () => Promise<T>) => Promise<T>;
  readonly publish: () => void;
  readonly subscribe: (onChange: () => void) => () => void;
}

const recordKey = (id: string) => ["record", id];
const audioKey = (id: string) => ["audio", id];
const chunkRange = (id: string) => IDBKeyRange.bound(["chunk", id], ["chunk", id, []]);

export function createIndexedDbVoiceClipPersistence(store?: UseStore): VoiceClipPersistence {
  if (!store) {
    const name = accountDatabaseName("t3code-voice-clips");
    if (name === null) return createMemoryVoiceClipPersistence();
    store = createVoiceClipStore(name);
  }
  const entries = store;
  const deleteChunks = async (id: string) => {
    await entries("readwrite", (objectStore) =>
      promisifyRequest(objectStore.delete(chunkRange(id))),
    );
  };
  return {
    loadRecords: async () => {
      const all = await entries("readonly", (objectStore) =>
        promisifyRequest(objectStore.getAll(IDBKeyRange.bound(["record", ""], ["record", []]))),
      );
      return all as VoiceClipRecord[];
    },
    loadRecord: async (id) => get<VoiceClipRecord>(recordKey(id), entries),
    saveRecord: async (record) => set(recordKey(record.id), record, entries),
    appendChunk: async (id, index, chunk) => set(["chunk", id, index], chunk, entries),
    loadChunks: async (id) => {
      const chunks = await entries("readonly", (objectStore) =>
        promisifyRequest(objectStore.getAll(chunkRange(id))),
      );
      return chunks as Blob[];
    },
    saveAudio: async (id, audio) => {
      await set(audioKey(id), audio, entries);
      await deleteChunks(id);
    },
    loadAudio: async (id) => get<Blob>(audioKey(id), entries),
    remove: async (id) => {
      await del(recordKey(id), entries);
      await del(audioKey(id), entries);
      await deleteChunks(id);
    },
  };
}

function createVoiceClipStore(name: string): UseStore {
  let entries = createStore(name, "entries");
  return (mode, callback) =>
    entries(mode, (objectStore) => {
      const database = objectStore.transaction.db;
      // Sign-out and account changes delete databases while other tabs can hold connections.
      database.onversionchange = () => {
        database.close();
        entries = createStore(name, "entries");
      };
      return callback(objectStore);
    });
}

export function createMemoryVoiceClipPersistence(): VoiceClipPersistence {
  const records = new Map<string, VoiceClipRecord>();
  const audio = new Map<string, Blob>();
  const chunks = new Map<string, Blob[]>();
  return {
    loadRecords: async () => [...records.values()],
    loadRecord: async (id) => records.get(id),
    saveRecord: async (record) => {
      records.set(record.id, record);
    },
    appendChunk: async (id, index, chunk) => {
      const list = chunks.get(id) ?? [];
      list[index] = chunk;
      chunks.set(id, list);
    },
    loadChunks: async (id) => (chunks.get(id) ?? []).filter(Boolean),
    saveAudio: async (id, blob) => {
      audio.set(id, blob);
      chunks.delete(id);
    },
    loadAudio: async (id) => audio.get(id),
    remove: async (id) => {
      records.delete(id);
      audio.delete(id);
      chunks.delete(id);
    },
  };
}

const lockName = (id: string) => `t3code-voice-clip:${id}`;
const CHANNEL_NAME = "t3code-voice-clips";

export function createBrowserVoiceClipCoordination(): VoiceClipCoordination {
  const locks = typeof navigator !== "undefined" ? navigator.locks : undefined;
  if (!locks) return createLocalVoiceClipCoordination();
  let channel: BroadcastChannel | null | undefined;
  const openChannel = () => {
    channel ??= typeof BroadcastChannel !== "undefined" ? new BroadcastChannel(CHANNEL_NAME) : null;
    return channel;
  };
  return {
    hold: (id) =>
      new Promise((resolveHold) => {
        const settled = locks.request(lockName(id), { ifAvailable: true }, (lock) => {
          if (!lock) {
            resolveHold(null);
            return undefined;
          }
          return new Promise<void>((unlock) => {
            resolveHold(async () => {
              unlock();
              await settled;
            });
          });
        });
      }),
    withLock: (id, run) => locks.request(lockName(id), () => run()),
    publish: () => openChannel()?.postMessage("changed"),
    subscribe: (onChange) => {
      const opened = openChannel();
      if (!opened) return () => undefined;
      const listener = () => onChange();
      opened.addEventListener("message", listener);
      return () => opened.removeEventListener("message", listener);
    },
  };
}

export function createLocalVoiceClipCoordination(): VoiceClipCoordination {
  const held = new Map<string, Promise<void>>();
  const acquire = (id: string): VoiceClipLockRelease => {
    let resolve: () => void = () => undefined;
    const promise = new Promise<void>((settle) => {
      resolve = settle;
    });
    held.set(id, promise);
    return async () => {
      if (held.get(id) === promise) held.delete(id);
      resolve();
    };
  };
  return {
    hold: async (id) => (held.has(id) ? null : acquire(id)),
    withLock: async (id, run) => {
      for (let current = held.get(id); current; current = held.get(id)) {
        await current;
      }
      const release = acquire(id);
      try {
        return await run();
      } finally {
        await release();
      }
    },
    publish: () => undefined,
    subscribe: () => () => undefined,
  };
}
